import { Route } from "react-router-dom";
import {
  lazy,
  Suspense,
  type LazyExoticComponent,
  type ComponentType,
} from "react";

const RootContent = lazy(() => import("@/pages/_content"));
const VerifyCodeContent = lazy(() => import("@/pages/verify-code/_content"));
const JoinContent = lazy(() => import("@/pages/join/_content"));
const ParentAppOpenContent = lazy(() => import("@/pages/app/open/_content"));
const InviteAcceptContent = lazy(
  () => import("@/pages/invite/accept/_content")
);
const StaffInviteAcceptContent = lazy(
  () => import("@/pages/staff/invite/accept/_content")
);
const FamilyInviteAcceptContent = lazy(
  () => import("@/pages/family/invite/accept/_content")
);
const NewPasswordContent = lazy(() => import("@/pages/new-password/_content"));
const ParentNewPasswordContent = lazy(
  () => import("@/pages/parent/new-password/_content")
);
const AccountRegisterContent = lazy(
  () => import("@/pages/account/register/_content")
);
const AccountVerifyEmailContent = lazy(
  () => import("@/pages/account/verify-email/_content")
);
const TermsPage = lazy(() => import("@/pages/terms/page"));
const PrivacyPage = lazy(() => import("@/pages/privacy/page"));
const TrialFormPage = lazy(() => import("@/pages/trial/[facilityId]/page"));
const EmbedCalendarPage = lazy(
  () => import("@/pages/embed/calendar/[facilityId]/page")
);
const PublicSurveyPage = lazy(
  () => import("@/pages/survey/public/[token]/page")
);
const SurveyPage = lazy(() => import("@/pages/survey/[token]/page"));
const EventCancellationPage = lazy(
  () => import("@/pages/events/cancel/[token]/page")
);
const EventPublicApplyPage = lazy(
  () => import("@/pages/events/[id]/apply/page")
);
const EventReservationScheduleApplyPage = lazy(
  () => import("@/pages/event-schedules/[id]/apply/page")
);
const EventPaymentLinkPage = lazy(
  () => import("@/pages/events/payment/[token]/page")
);
const TuitionMigrationPage = lazy(
  () => import("@/pages/payment/tuition-migration/[subscriptionId]/page")
);
const PaymentRegisterPage = lazy(() => import("@/pages/payment/register/page"));
const TagPatternsPage = lazy(
  () => import("@/pages/(authenticated)/dev/tag-patterns/page")
);
const DialogPatternsPage = lazy(
  () => import("@/pages/(authenticated)/dev/dialog-patterns/page")
);
const DescriptionPatternsPage = lazy(
  () => import("@/pages/(authenticated)/dev/description-patterns/page")
);
const SuspensionRulesDemo = lazy(
  () => import("@/pages/(authenticated)/dev/suspension-rules/page")
);

const Loading = () => <div style={{ padding: 24 }}>Loading...</div>;

const wrap = (C: LazyExoticComponent<ComponentType>) => (
  <Suspense fallback={<Loading />}>
    <C />
  </Suspense>
);

export const publicRoutes = (
  <>
    {import.meta.env.DEV ? (
      <>
        <Route
          path="/dev/suspension-rules"
          element={wrap(SuspensionRulesDemo)}
        />
        <Route path="/dev/tag-patterns" element={wrap(TagPatternsPage)} />
        <Route path="/dev/dialog-patterns" element={wrap(DialogPatternsPage)} />
        <Route
          path="/dev/description-patterns"
          element={wrap(DescriptionPatternsPage)}
        />
      </>
    ) : null}
    <Route path="/" element={wrap(RootContent)} />
    <Route path="/verify-code" element={wrap(VerifyCodeContent)} />
    <Route path="/join" element={wrap(JoinContent)} />
    <Route path="/app/open" element={wrap(ParentAppOpenContent)} />
    <Route path="/app/open/dev" element={wrap(ParentAppOpenContent)} />
    <Route path="/invite/accept" element={wrap(InviteAcceptContent)} />
    <Route
      path="/family/invite/accept"
      element={wrap(FamilyInviteAcceptContent)}
    />
    <Route
      path="/staff/invite/accept"
      element={wrap(StaffInviteAcceptContent)}
    />
    <Route path="/new-password" element={wrap(NewPasswordContent)} />
    <Route
      path="/parent/new-password"
      element={wrap(ParentNewPasswordContent)}
    />
    <Route path="/account/register" element={wrap(AccountRegisterContent)} />
    <Route
      path="/account/verify-email"
      element={wrap(AccountVerifyEmailContent)}
    />
    <Route path="/terms" element={wrap(TermsPage)} />
    <Route path="/privacy" element={wrap(PrivacyPage)} />
    <Route path="/trial/:facilityId" element={wrap(TrialFormPage)} />
    <Route
      path="/embed/calendar/:facilityId"
      element={wrap(EmbedCalendarPage)}
    />
    <Route path="/survey/public/:token" element={wrap(PublicSurveyPage)} />
    <Route path="/survey/:token" element={wrap(SurveyPage)} />
    <Route path="/events/cancel/:token" element={wrap(EventCancellationPage)} />
    {/* 公開の申込＋決済ページ。認証済みの /events/:id（管理画面）とはセグメント数で
        競合しないため、同じ Routes に並べても取り違えない。 */}
    <Route path="/events/:id/apply" element={wrap(EventPublicApplyPage)} />
    <Route
      path="/event-schedules/:id/apply"
      element={wrap(EventReservationScheduleApplyPage)}
    />
    <Route path="/events/payment/:token" element={wrap(EventPaymentLinkPage)} />
    <Route
      path="/payment/tuition-migration/:subscriptionId"
      element={wrap(TuitionMigrationPage)}
    />
    <Route path="/payment/register" element={wrap(PaymentRegisterPage)} />
  </>
);
